import React from 'react';
import PropTypes from 'prop-types';

import {
  ActionRow,
  Dropdown,
  Button,
  Icon,
} from '@openedx/paragon';

import { Check } from '@openedx/paragon/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from '@edx/frontend-platform/i18n';
import { thunkActions, selectors } from '../../../../../../data/redux';
import { videoTranscriptLanguages } from '../../../../../../data/constants/video';
import { FileInput, fileInput } from '../../../../../../sharedComponents/FileInput';
import messages from './messages';

export const hooks = {
  onSelectLanguage: ({
    dispatch,
    languageBeforeChange,
    triggerupload,
    setLocalLang,
  }) =>
  ({ newLang }) => {
    // IF Language is unset, set language and begin upload prompt.
    setLocalLang(newLang);
    if (languageBeforeChange === '') {
      triggerupload();
      return;
    }
    // Else: update language
    dispatch(
      thunkActions.video.updateTranscriptLanguage({
        newLanguageCode: newLang,
        languageBeforeChange,
      }),
    );
  },

  addFileCallback: ({ dispatch, localLang }) => (file) => {
    dispatch(thunkActions.video.uploadTranscript({
      file,
      filename: file.name,
      language: localLang,
    }));
  },
};

const LanguageSelector = ({
  index, // For a unique id for the form control
  language,
}) => {
  const openLanguages = useSelector(selectors.video.openLanguages);
  const intl = useIntl();
  const [localLang, setLocalLang] = React.useState(language);
  const input = fileInput({ onAddFile: hooks.addFileCallback({ dispatch: useDispatch(), localLang }) });
  const onLanguageChange = hooks.onSelectLanguage({
    dispatch: useDispatch(),
    languageBeforeChange: localLang,
    setLocalLang,
    triggerupload: input.click,
  });

  const getTitle = () => {
    if (Object.prototype.hasOwnProperty.call(videoTranscriptLanguages, language)) {
      return (
        <ActionRow>
          {videoTranscriptLanguages[language]}
          <ActionRow.Spacer />
          <Icon className="text-primary-500" src={Check} />
        </ActionRow>
      );
    }
    return (
      <ActionRow>
        {intl.formatMessage(messages.languageSelectPlaceholder)}
        <ActionRow.Spacer />
      </ActionRow>
    );
  };

  return (
    <>
      <Dropdown className="w-100 mb-2">
        <Dropdown.Toggle
          iconAs={Button}
          aria-label={intl.formatMessage(messages.languageSelectLabel)}
          block
          id={`selectLanguage-form-${index}`}
          className="w-100"
          variant="outline-primary"
        >
          {getTitle()}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {Object.entries(videoTranscriptLanguages).map(([lang, text]) => {
            if (language === lang) {
              return (
                <Dropdown.Item key={lang}>
                  {text}
                  <Icon className="text-primary-500" src={Check} />
                </Dropdown.Item>
              );
            }
            if (openLanguages.some(row => row.includes(lang))) {
              return (
                <Dropdown.Item key={lang} onClick={() => onLanguageChange({ newLang: lang })}>{text}</Dropdown.Item>
              );
            }
            return <Dropdown.Item key={lang} className="disabled">{text}</Dropdown.Item>;
          })}
        </Dropdown.Menu>
      </Dropdown>
      <FileInput fileInput={input} acceptedFiles=".srt" />
    </>
  );
};

LanguageSelector.propTypes = {
  index: PropTypes.number.isRequired,
  language: PropTypes.string.isRequired,
};

export default LanguageSelector;
